import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { asDecimalString } from "@shop-ledger/shared-types";
import { Button, DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { getPurchaseOrder, receivePurchaseOrder } from "../../api/purchaseOrdersApi";
import type { PurchaseOrderLineOut, ReceiveLine } from "../../api/types";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { canWritePurchaseOrders } from "../../roles/permissions";
import { isFullyReceived, remainingQuantity } from "../../domain/purchaseOrders";

/**
 * Purchase order detail + receive action — `GET /purchase-orders/{id}`,
 * `POST /purchase-orders/{id}/receive` (§10.4). Receipt quantities are DELTAS for this
 * receipt, not cumulative totals, so partial receiving works: call this again later with
 * the remaining quantity.
 */
export function PurchaseOrderDetailPage() {
  const { poId } = useParams<{ poId: string }>();
  const { user, ensureFreshToken } = useAuth();
  const canReceive = user ? canWritePurchaseOrders(user.role) : false;
  const poState = useAsyncData(() => ensureFreshToken().then((token) => getPurchaseOrder(token, poId!)), [poId]);

  const [receiveQty, setReceiveQty] = useState<Record<string, string>>({});
  const [receiveBatchNumber, setReceiveBatchNumber] = useState<Record<string, string>>({});
  const [receiveExpiry, setReceiveExpiry] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleReceive(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSuccess(null);
    const lines: ReceiveLine[] = Object.entries(receiveQty)
      .filter(([, qty]) => qty && Number(qty) > 0)
      .map(([lineId, qty]) => ({
        line_id: lineId,
        quantity_received: asDecimalString(qty),
        batch_number: receiveBatchNumber[lineId] || undefined,
        expiry_date: receiveExpiry[lineId] || undefined,
      }));
    if (lines.length === 0) {
      setFormError("Enter a received quantity for at least one line.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await receivePurchaseOrder(token, poId!, { lines });
      setReceiveQty({});
      setReceiveBatchNumber({});
      setReceiveExpiry({});
      setSuccess("Receipt recorded — stock updated.");
      poState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not record receipt.");
    } finally {
      setSubmitting(false);
    }
  }

  if (poState.loading) return <div className="page">Loading…</div>;
  if (poState.error) return <div className="page"><StatusBanner kind="error">{poState.error}</StatusBanner></div>;
  if (!poState.data) return <div className="page">Purchase order not found.</div>;

  const po = poState.data;
  const isReceivable = canReceive && (po.status === "ORDERED" || po.status === "PARTIALLY_RECEIVED");

  const columns: DataTableColumn<PurchaseOrderLineOut, string>[] = [
    { key: "inventory_item_id", header: "Item", render: (r) => <span className="muted">{r.inventory_item_id.slice(0, 8)}</span> },
    { key: "quantity_ordered", header: "Ordered", align: "right", render: (r) => r.quantity_ordered },
    { key: "quantity_received", header: "Received", align: "right", render: (r) => r.quantity_received },
    {
      key: "unit_cost",
      header: "Remaining",
      align: "right",
      render: (r) => remainingQuantity(r.quantity_ordered, r.quantity_received),
    },
    {
      key: "receive_qty",
      header: "Receive now",
      render: (r) => {
        const done = isFullyReceived(r.quantity_ordered, r.quantity_received);
        return isReceivable && !done ? (
          <input
            className="sl-input"
            style={{ width: 100 }}
            inputMode="decimal"
            placeholder="0"
            value={receiveQty[r.id] ?? ""}
            onChange={(e) => setReceiveQty((prev) => ({ ...prev, [r.id]: e.target.value }))}
          />
        ) : (
          <span className="muted">{done ? "fully received" : "—"}</span>
        );
      },
    },
    {
      key: "receive_batch_number",
      header: "Batch # (optional)",
      render: (r) => {
        const done = isFullyReceived(r.quantity_ordered, r.quantity_received);
        return isReceivable && !done ? (
          <input
            className="sl-input"
            style={{ width: 120 }}
            placeholder="e.g. LOT-1"
            value={receiveBatchNumber[r.id] ?? ""}
            onChange={(e) => setReceiveBatchNumber((prev) => ({ ...prev, [r.id]: e.target.value }))}
          />
        ) : null;
      },
    },
    {
      key: "receive_expiry",
      header: "Expiry (optional)",
      render: (r) => {
        const done = isFullyReceived(r.quantity_ordered, r.quantity_received);
        return isReceivable && !done ? (
          <input
            className="sl-input"
            type="date"
            style={{ width: 150 }}
            value={receiveExpiry[r.id] ?? ""}
            onChange={(e) => setReceiveExpiry((prev) => ({ ...prev, [r.id]: e.target.value }))}
          />
        ) : null;
      },
    },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Purchase Order {po.id.slice(0, 8)}</h1>
          <p className="page__subtitle">
            Branch <span className="muted">{po.branch_id.slice(0, 8)}</span> · Status{" "}
            <span className="badge badge--current">{po.status}</span>
          </p>
        </div>
      </div>

      {po.notes ? <p className="muted">Notes: {po.notes}</p> : null}

      {po.status === "PENDING_APPROVAL" ? (
        <StatusBanner kind="warning">
          This PO exceeded an approval-rule threshold and is awaiting a decision — it cannot be received until
          approved. Review it in the <Link to="/approvals">Approval Requests</Link> queue.
        </StatusBanner>
      ) : null}

      <form onSubmit={handleReceive}>
        <div className="panel">
          <h2 className="panel__title">Lines</h2>
          <DataTable columns={columns} rows={po.lines} getRowId={(row) => row.id} emptyMessage="No lines." caption="PO lines" />
          {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
          {success ? <StatusBanner kind="info">{success}</StatusBanner> : null}
          {isReceivable ? (
            <div className="form-actions" style={{ marginTop: 12 }}>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Recording…" : "Record receipt"}
              </Button>
            </div>
          ) : !canReceive ? (
            <p className="muted" style={{ marginTop: 12 }}>
              Your role ({user?.role}) doesn't have receiving access (OWNER/BRANCH_MANAGER/ECOM_MGR only).
            </p>
          ) : po.status !== "PENDING_APPROVAL" ? (
            <p className="muted" style={{ marginTop: 12 }}>
              This purchase order is {po.status.toLowerCase().replace("_", " ")} — nothing left to receive.
            </p>
          ) : null}
        </div>
      </form>
    </div>
  );
}
